import React, { useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { Flame, Target, Award, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface LevelInfo {
  level: number;
  title: string;
  xpRequired: number;
  cumulativeXp: number;
}

interface MicroStatsPanelProps {
  level?: number;
  xp?: number;
  nextLevelXp?: number;
  streak?: number;
  completed?: number;
  inProgress?: number;
  levelTitle?: string;
  levelProgression?: LevelInfo[];
}

// Default level progression with CUMULATIVE XP thresholds
// xpRequired = cumulative XP needed to reach the NEXT level
// cumulativeXp = cumulative XP needed to reach THIS level
const DEFAULT_LEVEL_PROGRESSION: LevelInfo[] = [
  { level: 1, title: 'Newcomer', xpRequired: 50, cumulativeXp: 0 },
  { level: 2, title: 'Curious Learner', xpRequired: 150, cumulativeXp: 50 },
  { level: 3, title: 'Active Explorer', xpRequired: 300, cumulativeXp: 150 },
  { level: 4, title: 'Prompt Crafter', xpRequired: 500, cumulativeXp: 300 },
  { level: 5, title: 'AI Whisperer', xpRequired: 750, cumulativeXp: 500 },
  { level: 6, title: 'Context Master', xpRequired: 1050, cumulativeXp: 750 },
  { level: 7, title: 'Pattern Spotter', xpRequired: 1400, cumulativeXp: 1050 },
  { level: 8, title: 'Iteration Pro', xpRequired: 1800, cumulativeXp: 1400 },
  { level: 9, title: 'Output Refiner', xpRequired: 2300, cumulativeXp: 1800 },
  { level: 10, title: 'Strategy Sage', xpRequired: 2900, cumulativeXp: 2300 },
  { level: 11, title: 'Workflow Wizard', xpRequired: 3600, cumulativeXp: 2900 },
  { level: 12, title: 'Efficiency Expert', xpRequired: 4400, cumulativeXp: 3600 },
  { level: 13, title: 'AI Architect', xpRequired: 5300, cumulativeXp: 4400 },
  { level: 14, title: 'Prompt Virtuoso', xpRequired: 6300, cumulativeXp: 5300 },
  { level: 15, title: 'AI Champion', xpRequired: 7500, cumulativeXp: 6300 },
];

export const MicroStatsPanel: React.FC<MicroStatsPanelProps> = ({
  level = 1,
  xp = 0,
  nextLevelXp = 50,
  streak = 0,
  completed = 0,
  inProgress = 0,
  levelTitle = 'Newcomer',
  levelProgression = DEFAULT_LEVEL_PROGRESSION
}) => {
  // Find current level threshold from progression data
  const currentLevelInfo = levelProgression.find(l => l.level === level);
  const currentThreshold = currentLevelInfo?.cumulativeXp || 0;

  // Calculate progress within current level (cumulative XP system)
  // Progress = (currentXP - currentLevelThreshold) / (nextLevelThreshold - currentLevelThreshold)
  const xpIntoLevel = Math.max(0, xp - currentThreshold);
  const xpNeededForLevel = Math.max(1, nextLevelXp - currentThreshold);
  const levelProgress = Math.min(100, (xpIntoLevel / xpNeededForLevel) * 100);

  // XP Tooltip content showing level progression with cumulative thresholds
  const XPTooltipContent = () => (
    <div className="w-80 p-3">
      <p className="text-sm font-medium mb-2 text-slate-700">Level Progression (Cumulative XP)</p>
      <div className="space-y-0.5 max-h-64 overflow-y-auto pr-1">
        {levelProgression.slice(0, 15).map((lvl) => (
          <div
            key={lvl.level}
            className={`flex items-center justify-between text-[11px] py-1 px-2 rounded ${
              lvl.level === level
                ? 'bg-indigo-100 text-indigo-700 font-medium'
                : lvl.level < level
                ? 'text-slate-400 line-through decoration-slate-300'
                : 'text-slate-600'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span className="w-3 text-center">{lvl.level < level ? '✓' : lvl.level === level ? '→' : ''}</span>
              <span className="font-medium w-6">Lv.{lvl.level}</span>
              <span className="text-[10px] opacity-80 truncate max-w-[90px]">{lvl.title}</span>
            </div>
            <span className="tabular-nums text-right">
              {lvl.level === 1 ? '0' : lvl.cumulativeXp.toLocaleString()}–{lvl.xpRequired.toLocaleString()} XP
            </span>
          </div>
        ))}
        {levelProgression.length > 15 && (
          <div className="text-[10px] text-slate-400 text-center py-1">
            +{levelProgression.length - 15} more levels...
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6 flex flex-col gap-6 text-slate-900 shadow-sm">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Level</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <p className="text-3xl font-semibold">{level}</p>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
              <Award className="w-3 h-3" />
              {levelTitle}
            </span>
          </div>
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="text-xs text-slate-500 cursor-help hover:text-indigo-600 transition-colors flex items-center gap-1">
                  {xp} / {nextLevelXp} XP
                  <Info className="w-3 h-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-white border border-slate-200 shadow-lg p-0">
                <XPTooltipContent />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Progress value={levelProgress} className="mt-3 h-2" />
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Streak</span>
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <Flame className="w-4 h-4 text-slate-600" />
            {streak} days
          </div>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Sessions</span>
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <Target className="w-4 h-4 text-slate-600" />
            {completed} done • {inProgress} active
          </div>
        </div>
      </div>
    </div>
  );
};
